import type { ReactNode } from 'react';
import { LateralityPicker } from '@/components/ui/laterality-picker';
import { IMAGE_TYPES, IMAGE_TYPE_LABELS, isLaterality, typeAllowed, type ImageKind, type ImageType } from '../../data/imageTypes';
import ANATOMIC_SITES from '../data/anatomic-sites.json';
import STAINS from '../data/stains.json';
import { AttachedButton, AttachedInput, AttachedTag, BareInput, Field, Input, Textarea } from '../ui/Field';
import { IconWand } from '../ui/icons';
import { Select, type SelectOption } from '../ui/Select';
import { phiHint, type UploadFile, type UploadSession } from './store';

/**
 * The study's and the image's detail fields, as the uploader draws them
 * (step 1's New study form, step 2's per-image panel) and the details editor
 * reuses them for a study that is already published. The pages own the
 * values, the saving and the refusals; these own only the markup, so the two
 * cannot drift apart.
 */
export const SITE_OPTIONS: SelectOption[] = (ANATOMIC_SITES as Array<{ value: string; label: string; hint: string }>).map((o) => ({ value: o.value, label: o.label, hint: o.hint }));
export const STAIN_OPTIONS: SelectOption[] = (STAINS as Array<{ value: string; label: string; hint: string }>).map((o) => ({ value: o.value, label: o.label, hint: o.hint }));
/**
 * All seven Types (data/imageTypes.ts), those the file certainly cannot be
 * greyed: a scanner's slide is no gross photo. A file not known yet (null: a
 * JPEG may be pyramidised into a slide, a DICOM file can be either) may be any.
 */
export const typeOptions = (certain: ImageKind | null, certainType: ImageType | null = null): SelectOption[] =>
  IMAGE_TYPES.map((t) => ({ value: t, label: IMAGE_TYPE_LABELS[t], disabled: !typeAllowed(t, certain, certainType) }));
export const SEX_OPTIONS: SelectOption[] = [{ value: 'F', label: 'Female' }, { value: 'M', label: 'Male' }, { value: 'O', label: 'Other' }];

export const meaningOf = (opts: SelectOption[], value: string) => opts.find((o) => o.value === value)?.label ?? '';

export type StudyFieldValues = Pick<UploadSession, 'label' | 'sex' | 'age' | 'diagnosis' | 'desc'>;
export type ImageFieldKey = 'type' | 'site' | 'siteMeaning' | 'laterality' | 'stain' | 'stainMeaning' | 'specDesc';
export type ImageFieldValues = Pick<UploadFile, ImageFieldKey>;

/**
 * What is said under a field: the server's refusal when it named this field,
 * else the client's early word on identifier-like text (`hint`), else nothing.
 */
export type FieldHint = (field: string, hint: string | null) => ReactNode;

/** The client's early word on identifier-like text in the study's free text. */
export const studyHints = (v: Pick<StudyFieldValues, 'diagnosis' | 'desc'>) => ({ diagnosis: phiHint(v.diagnosis), description: phiHint(v.desc) });

/**
 * Study ID, sex, age, diagnosis and description. `onAgeBlur` settles a typed
 * age into 0 to 90 (the uploader does it without clearing a refusal);
 * `onGenerate` adds the wand that suggests a Study ID.
 */
export function StudyFields({
  values,
  onChange,
  onAgeBlur,
  onGenerate,
  said,
}: {
  values: StudyFieldValues;
  onChange: (p: Partial<StudyFieldValues>) => void;
  onAgeBlur: (age: string) => void;
  onGenerate?: () => void;
  said: FieldHint;
}) {
  const hints = studyHints(values);
  const ageCapped = Number(values.age) >= 90;
  return (
    <div className="tr-stack-sm" style={{ display: 'grid', gridTemplateColumns: 'minmax(180px,1fr) 180px 120px', gap: '14px 16px', alignItems: 'start' }}>
      <Field label="Study ID" required hint={said('study_id', null)}>
        <AttachedInput>
          <BareInput mono value={values.label} onChange={(e) => onChange({ label: e.target.value.slice(0, 16) })} spellCheck={false} maxLength={16} />
          {onGenerate ? (
            <AttachedButton title="Generate an ID" onClick={onGenerate}>
              <IconWand size={14} />
            </AttachedButton>
          ) : null}
        </AttachedInput>
      </Field>
      <Field label="Sex">
        <Select value={values.sex} options={SEX_OPTIONS} onChange={(v) => onChange({ sex: v })} placeholder="Select…" size="lg" width="100%" />
      </Field>
      <Field label="Age" hint={said('age', null)}>
        <AttachedInput>
          <BareInput
            type="number"
            min={0}
            max={90}
            value={values.age}
            onChange={(e) => onChange({ age: e.target.value })}
            onBlur={(e) => {
              const v = e.target.value;
              onAgeBlur(v === '' ? '' : String(Math.max(0, Math.min(90, parseInt(v, 10) || 0))));
            }}
            placeholder="0 to 90+"
          />
          {ageCapped ? <AttachedTag>90+</AttachedTag> : null}
        </AttachedInput>
      </Field>
      <Field label="Diagnosis" span hint={said('diagnosis', hints.diagnosis)}>
        <Input value={values.diagnosis} onChange={(e) => onChange({ diagnosis: e.target.value })} maxLength={64} />
      </Field>
      <Field label="Description" span hint={said('description', hints.description)}>
        <Textarea value={values.desc} onChange={(e) => onChange({ desc: e.target.value })} maxLength={64} />
      </Field>
    </div>
  );
}

/**
 * An image's Name (its Series Description, what the viewer's image cards
 * show), then its anatomic site and laterality, Type and stain. Choosing a
 * site or a stain sets its meaning too; the laterality is said in any order
 * with the site (the file carries it once there is one). Every Type is
 * offered, less those the file `certain`ly cannot be (null: not known yet,
 * any); a document (a PDF) needs no site, since it is the whole case's.
 * `disabled` shows the values of an image that cannot be edited yet.
 */
export function ImageFields({
  values,
  certain,
  certainType = null,
  onField,
  said,
  disabled,
}: {
  values: ImageFieldValues;
  certain: ImageKind | null;
  /** The one Type the file can be, when its header says (an X-ray is Radiography). */
  certainType?: ImageType | null;
  onField: (k: ImageFieldKey, v: string) => void;
  said: FieldHint;
  disabled?: boolean;
}) {
  return (
    <div className="tr-stack-sm" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px' }}>
      <Field label="Name" span hint={said('description', phiHint(values.specDesc))}>
        <Input value={values.specDesc} disabled={disabled} onChange={(e) => onField('specDesc', e.target.value)} maxLength={64} />
      </Field>
      <Field label="Anatomic site" required={certain !== 'document'} hint={said('site', null)}>
        <Select value={values.site} options={SITE_OPTIONS} disabled={disabled} onChange={(v) => { onField('site', v); onField('siteMeaning', meaningOf(SITE_OPTIONS, v)); }} placeholder="Search SNOMED CT…" size="lg" width="100%" />
      </Field>
      <Field label="Laterality" hint={said('laterality', null)}>
        <LateralityPicker value={isLaterality(values.laterality) ? values.laterality : null} disabled={disabled} onChange={(v) => onField('laterality', v ?? '')} size="md" />
      </Field>
      <Field label="Type" hint={said('type', null)}>
        <Select value={values.type} options={typeOptions(certain, certainType)} disabled={disabled} onChange={(v) => onField('type', v)} placeholder="Select…" size="lg" width="100%" />
      </Field>
      <Field label="Stain / preparation" hint={said('stain', null)}>
        <Select value={values.stain} options={STAIN_OPTIONS} disabled={disabled} onChange={(v) => { onField('stain', v); onField('stainMeaning', meaningOf(STAIN_OPTIONS, v)); }} placeholder="Search…" size="lg" width="100%" />
      </Field>
    </div>
  );
}
